import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'

import auth from './modules/auth'
import downloading from './modules/downloading'
import contracts from './modules/contracts'
import filters from './modules/filters'
import notifications from './modules/notifications'
import socket from './modules/socket'
import activeTasks from './modules/active-tasks'
import experience from './modules/experience'
import reports from './modules/reports'
import paymentsControl from './modules/payments-control'
import dashboard from './modules/dashboard'
import { mainUrl } from '../config/urls'
import { toggleTheme, getDataFromLS } from '../utils'

const permissions = getDataFromLS('permissions', null)
const theme = getDataFromLS('theme', 'light')
toggleTheme(theme)

Vue.use(Vuex)

const errorHandlerFactory = (dispatch, reject) => err => {
  if (err && err.response) {
    const { data } = err.response
    const { error } = data

    if (error <= 4) {
      window.stop()
      dispatch('AUTH_LOGOUT')
    }
  }
  reject(err)
}

export default new Vuex.Store({
  state: {
    appLoading: false,
    companies: [],
    users: [],
    positions: [],
    contractStatuses: [],
    selectedMode: false,
    selectedToExport: [],
    aboutModal: false,
    permissions,
    theme
  },
  getters: {
    getPermissionModule: state => type => state.permissions[type],
    hasModule: state => module => {
      if (!state.permissions) return false

      switch (module) {
        case 'books':
          return Object.values(state.permissions.books).some(b => b && b.read)
        case 'contract_acts':
        case 'contract_payments':
        case 'contragents':
        case 'amendments':
          return state.permissions.contract[module].read
        case 'time-table':
        case 'contract_register':
          if (!state.permissions[module]) return false

          return state.permissions[module].read
        default:
          if (!state.permissions[module]) return false

          return !!state.permissions[module].read
      }
    },
    getCommonModulePerms: state => (module, type) => {
      if (!state.permissions) return false

      return state.permissions[module][type]
    },
    getContractModulePerms: state => (module, type) => {
      if (!state.permissions) return false

      return state.permissions.contract[module][type]
    }
  },
  mutations: {
    SET_APP_LOADING (state, value) {
      state.appLoading = value
    },
    SET_THEME (state, theme) {
      state.theme = theme
    },
    LOGOUT (state) {
      state.auth.token = ''
      state.auth.user = null
    },
    SET_COMPANIES (state, companies) {
      state.companies = companies
    },
    SET_CONTRACT_STATUSES (state, statuses) {
      state.contractStatuses = statuses
    },
    SET_USERS (state, users) {
      state.users = users
    },
    SET_POSITIONS (state, positions) {
      state.positions = positions
    },
    TOGGLE_SELECTED_MODE (state, value) {
      state.selectedMode = value
    },
    TOGGLE_SELECTED_CONTRACT (state, id) {
      const index = state.selectedToExport.indexOf(id)

      if (index === -1) state.selectedToExport.push(id)
      else state.selectedToExport.splice(index, 1)
    },
    CLEAR_SELECTED_CONTRACTS (state) {
      state.selectedToExport = []
    },
    SET_ABOUT_MODAL (state, value) {
      state.aboutModal = value
    },
    SET_PERMISSIONS (state, value) {
      state.permissions = value
    }
  },
  actions: {
    GET_REQUEST ({ state, commit }, { url }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${mainUrl}/api/${url}`)
          .then(({ data }) => resolve(data))
          .catch(errorHandlerFactory(commit, reject))
      })
    },
    POST_REQUEST ({ state, commit }, { url, data }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${mainUrl}/api/${url}`, data)
          .then(({ data }) => resolve(data))
          .catch(errorHandlerFactory(commit, reject))
      })
    },
    PUT_REQUEST ({ state, commit }, { url, data }) {
      return new Promise((resolve, reject) => {
        axios
          .put(`${mainUrl}/api/${url}`, data)
          .then(({ data }) => resolve(data))
          .catch(errorHandlerFactory(commit, reject))
      })
    },
    DELETE_REQUEST ({ state, commit }, { url, data }) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`${mainUrl}/api/${url}`)
          .then(({ data }) => resolve(data))
          .catch(errorHandlerFactory(commit, reject))
      })
    }
  },
  modules: {
    auth,
    downloading,
    contracts,
    filters,
    notifications,
    socket,
    activeTasks,
    experience,
    reports,
    paymentsControl,
    dashboard
  }
})
